/**
 * Chrome for the pages a person sees before they have an account: the landing
 * page, the support and contact pages, and the legal ones.
 *
 * Every link in here points at something that exists. The footer is the main
 * reason this is shared rather than repeated — a column of links is exactly the
 * thing that rots when each page keeps its own copy.
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../session.js';

/** Section anchors on the landing page, so the nav works from anywhere. */
const NAV: [string, string][] = [
  ['Overview', '/#how'],
  ['Checks', '/#checks'],
  ["Who it's for", '/#who'],
  ['Pricing', '/#pricing'],
];

const FOOTER: [string, [string, string][]][] = [
  ['Product', [
    ['Resource grid', '/support#grid'],
    ['Student portal', '/support#portal'],
    ['Maintenance', '/support#maintenance'],
    ['Billing', '/support#billing'],
  ]],
  ['For', [
    ['Freelance CFIs', '/#who-freelance'],
    ['Part 61 schools', '/#who-part61'],
    ['Part 141 schools', '/#who-part141'],
    ['Pricing', '/#pricing'],
  ]],
  ['Support', [
    ['Help centre', '/support'],
    ['Contact us', '/contact'],
    ['System status', '/status'],
    ['Getting your data out', '/support#export'],
  ]],
  ['Company', [
    ['About', '/about'],
    ['Privacy', '/privacy'],
    ['Terms', '/terms'],
    ['Security', '/security'],
  ]],
];

export function PublicHeader(): JSX.Element {
  const [theme, toggleTheme] = useTheme();
  const { pathname } = useLocation();
  const onLanding = pathname === '/';

  return (
    <header className="topbar">
      {/* On the landing page the mark is a scroll to the top; anywhere else it
          is the way back to it. */}
      {onLanding ? (
        <button
          className="topbar__brand"
          aria-label="Back to the top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <span className="topbar__mark" />FlightSlots
        </button>
      ) : (
        <Link to="/" className="topbar__brand" style={{ textDecoration: 'none' }}>
          <span className="topbar__mark" />FlightSlots
        </Link>
      )}
      <nav className="lp__nav">
        {NAV.map(([label, href]) => (
          <a key={label} href={href}>{label}</a>
        ))}
        <Link to="/support">Support</Link>
      </nav>
      <div className="topbar__right">
        <button className="chip" onClick={toggleTheme}>{theme === 'dark' ? 'Light' : 'Dark'}</button>
        <Link to="/login" className="chip" style={{ textDecoration: 'none' }}>Sign in</Link>
        <Link to="/signup" className="chip chip--on" style={{ textDecoration: 'none' }}>Free trial</Link>
      </div>
    </header>
  );
}

export function PublicFooter(): JSX.Element {
  return (
    <footer className="lp__footer">
      <div className="lp__fcols">
        <div style={{ maxWidth: '22em' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700 }}>
            <span className="topbar__mark" style={{ width: 15, height: 15, borderRadius: 5 }} />FlightSlots
          </div>
          <div style={{ fontSize: 12, color: 'var(--label-3)', marginTop: 8, textWrap: 'pretty' }}>
            Flight scheduling for freelance instructors and Part 61 and 141 flight schools.
          </div>
          <Link to="/contact" className="btn btn--sm" style={{ marginTop: 14, textDecoration: 'none' }}>
            Get in touch
          </Link>
        </div>
        {FOOTER.map(([heading, items]) => (
          <div className="lp__fcol" key={heading}>
            <div>{heading}</div>
            {items.map(([label, href]) => (
              <div className="lp__flink" key={label}>
                {href.startsWith('/#')
                  ? <a href={href}>{label}</a>
                  : <Link to={href}>{label}</Link>}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="lp__rule" style={{ margin: '26px 0 14px' }} />
      <div className="lp__legal">
        <span>© 2026 FlightSlots</span>
        <span>FlightSlots is a scheduling record, not an airworthiness or currency determination.</span>
      </div>
    </footer>
  );
}

/**
 * A prose page — support, legal, about. `lede` sits under the title, and the
 * body is measured rather than full width, because a legal paragraph at 1,000
 * pixels wide is a paragraph nobody finishes.
 */
export function PublicPage({ title, lede, updated, children }: {
  title: string;
  lede?: ReactNode;
  updated?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="lp">
      <PublicHeader />
      <div className="lp__doc">
        <h1 className="lp__doct">{title}</h1>
        {lede && <p className="lp__docl">{lede}</p>}
        {updated && <div className="lp__docu">Last updated {updated}</div>}
        {children}
      </div>
      <PublicFooter />
    </div>
  );
}

/** One titled block of a prose page, anchored so it can be linked to directly. */
export function DocSection({ id, title, children }: {
  id?: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="lp__docs">
      <h2 className="lp__doch">{title}</h2>
      {children}
    </section>
  );
}
